import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Avatar,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useIsDesktop } from 'Hooks';
import { AccordionArrowPurple } from 'Images';
import { requiredMessage } from 'Regex';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useController, UseControllerProps } from 'react-hook-form';
export interface Option {
  id: number | string;
  value: string;
}
interface IControllerAutoComplete extends UseControllerProps {
  label?: string;
  options: Option[];
  isReset?: React.MutableRefObject<boolean>;
}

export const MyPaper: FC<{}> = (props) => {
  return (
    <Paper
      sx={{
        borderRadius: `6px`,
      }}
    >
      {props.children}
    </Paper>
  );
};

export const AutoCompleteInput = (props: IControllerAutoComplete) => {
  const { label, options, ...controller } = props;
  const {
    fieldState: { error },
    field: { onChange, onBlur, ref, ...field },
  } = useController(controller);

  const [input, setInput] = useState<string>('');

  useEffect(() => {
    if (props.defaultValue?.value) setInput(props.defaultValue.value);
  }, [props.defaultValue]);

  const inputHandler = (
    e: React.SyntheticEvent<Element, Event>,
    v: string,
    r: AutocompleteInputChangeReason
  ) => {
    switch (r) {
      case 'clear':
        break;
      case 'reset':
        break;
      case 'input':
        setInput(v);
        break;
      default:
        break;
    }
  };

  const reset = useRef<string>('');

  const findOptionLabel = useCallback(
    (id: number) => {
      const option = options.find((option) => option.id === id);
      if (option) {
        setInput(option.value);
        return true;
      }
      return false;
    },
    [options]
  );

  useEffect(() => {
    if (field.value && reset.current !== 'DONE' && options.length) {
      const option = findOptionLabel(field.value);
      if (option) {
        reset.current = 'DONE';
      }
    }
  }, [field.value, findOptionLabel, options]);

  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (props.isReset?.current) {
      setInput(
        options.find((option) => option.id === field.value)?.value || ''
      );
      props.isReset.current = false;
    }
  }, [field.value, options, props.isReset]);

  return (
    <Autocomplete
      sx={{
        width: '100%',
        minWidth: isDesktop ? '400px' : undefined,
        '.MuiOutlinedInput-root': {
          height: isDesktop ? '48px' : '42px',
          mt: isDesktop ? '6px' : '7px',
          '&:hover fieldset': {
            borderColor: 'primary.main',
          },
        },
        '.MuiOutlinedInput-input': {
          mt: isDesktop ? '-4px' : '-7px',
        },
        '.MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.shade_200',
          borderRadius: '6px',
          '&:hover': {
            borderColor: 'primary.main',
          },
        },
        '&.MuiButtonBase-root[title="close"] > ': {
          // bgcolor: 'red',
        },
      }}
      {...field}
      value={field.value || null}
      id={label}
      onChange={(e, v, r) => {
        if (v) {
          setInput(v?.value);
          onChange(v ? v.id : v);
        }
      }}
      PaperComponent={MyPaper}
      noOptionsText={Dictionary[DICTIONARY.General].noOptions}
      options={options}
      inputValue={input}
      onBlur={() => {
        if (field.value) {
          findOptionLabel(field.value);
        } else {
          setInput('');
        }
        onBlur();
      }}
      onInputChange={inputHandler}
      disableClearable
      popupIcon={
        <Avatar
          alt=''
          src={AccordionArrowPurple}
          sx={{
            width: '32px',
            height: '32px',
            mt: '-4px',
          }}
        />
      }
      getOptionLabel={(option: Option) => option.value || ''}
      renderInput={(params) => (
        <TextField
          role={Boolean(error) ? 'alert' : undefined}
          inputRef={ref}
          label={label}
          error={Boolean(error)}
          helperText={error?.message}
          {...params}
          inputProps={{
            ...params.inputProps,
            'aria-required': error?.message === requiredMessage,
            'aria-label': `${label}. ${
              Dictionary[DICTIONARY.General].autocompleteDescription
            }`,
          }}
        />
      )}
      renderOption={(props, option) => (
        <Typography
          sx={{ '&&:hover': { bgcolor: 'primary.shade_100' } }}
          {...props}
        >
          {option.value}
        </Typography>
      )}
    />
  );
};
